import * as React from "react";

import { BubbleTag } from "@impero-com/ui";

import { DataExtractor } from "../../utils/dataView";
import { customOrDefaultFilterTransformer } from "../../utils/dataView/extractor";
import { t } from "../../utils/i18n";
import {
    SimpleAndFilter,
    SimpleFieldFilter,
    MATCH_TYPE_WILDCARD,
} from "../../utils/indexing";

import * as theme from "./theme.css";

/**
 * A component to display a simple filter as a bunch of tags, as well as a reset button to remove all the tags.
 *
 * Props:
 * - `filter`: the filter to display
 * - `getLabel`: returns the label of a field
 * - `onRemoveFilter`: a callback triggered when the user clicks on the "cross" on a given tag
 * - `onResetFilter`: a callback triggered when the user clicks on the reset button.
 */
class FilterTags extends React.Component<FilterTagsProps> {
    render() {
        const { filter, onResetFilter, getExtractor } = this.props;
        if (filter === null) {
            return null;
        }
        const columns = filter.filters.map((orFilter) => {
            const extractor = getExtractor(orFilter.extractorId);
            return (
                <span className={theme.orFilter} key={orFilter.extractorId}>
                    <span className={theme.orFilterCategory}>
                        {extractor.label}
                    </span>
                    {orFilter.filters?.map(
                        this.fieldFilterMapper(extractor, false)
                    )}
                    {orFilter.exclusionFilters?.map(
                        this.fieldFilterMapper(extractor, true)
                    )}
                </span>
            );
        });

        return (
            <div>
                {columns}
                <span className={theme.resetFilter}>
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            onResetFilter();
                        }}
                    >
                        {t("ControlResult_SearchTag_Reset")}
                    </a>
                </span>
            </div>
        );
    }

    private fieldFilterMapper<FV>(
        extractor: DataExtractor<unknown, unknown, FV>,
        excluded: boolean
    ) {
        const { toDisplayFilter, onRemoveFilter } = this.props;
        return (fieldFilter: SimpleFieldFilter) => {
            const untransformedFieldFilter = (
                toDisplayFilter
                    ? toDisplayFilter(extractor.id, fieldFilter)
                    : fieldFilter
            ) as SimpleFieldFilter<FV>;
            const filter = customOrDefaultFilterTransformer(extractor);
            let filterValueLabel: string;
            switch (untransformedFieldFilter.matchType) {
                case MATCH_TYPE_WILDCARD:
                    filterValueLabel =
                        "*" + untransformedFieldFilter.value + "*";
                    break;
                default:
                    filterValueLabel = filter.asString(
                        untransformedFieldFilter.value
                    );
                    break;
            }
            let tag: string;
            let className: string;
            let formattedTag: React.ReactNode | undefined;
            if (!excluded) {
                tag = filterValueLabel;
                className = theme.bubble;
                formattedTag = undefined;
            } else {
                tag = t("Filter_Tag_Exclude") + " " + filterValueLabel;
                className = theme.bubbleExcluded;
                formattedTag = (
                    <span>
                        <span className={theme.bubbleTagFlagExcluded}>
                            {t("Filter_Tag_Exclude")}
                        </span>{" "}
                        {filterValueLabel}
                    </span>
                );
            }
            return (
                <BubbleTag
                    key={`${excluded ? "excl" : "incl"}_${filterValueLabel}`}
                    category={extractor.label}
                    tag={tag}
                    formattedTag={formattedTag}
                    className={className}
                    categoryClassName={theme.bubbleCategory}
                    tagClassName={theme.bubbleTag}
                    onDelete={() =>
                        onRemoveFilter(extractor.id, fieldFilter, excluded)
                    }
                />
            );
        };
    }
}

interface FilterTagsProps {
    filter: SimpleAndFilter;
    getExtractor: (extractorId: string) => DataExtractor<unknown, unknown>;
    toDisplayFilter?: (
        extractorId: string,
        filter: SimpleFieldFilter
    ) => SimpleFieldFilter;
    onRemoveFilter: (
        extractorId: string,
        filter: SimpleFieldFilter,
        excluded: boolean
    ) => void;
    onResetFilter: () => void;
}

export { FilterTags, type FilterTagsProps };
